import { Location } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { FilterOptions } from '@app-model/filter';
import {
  FilterItem,
  FILTER_ACCOUNT_TYPES,
  FILTER_AGES,
  FILTER_GENRES,
  FILTER_PROOF_TYPES,
  FILTER_STATUS,
  SideFilterOptions,
  FILTER_STATUS_DEFAULT,  FILTER_ROLES
} from '@app-model/side-filter-options';
import { FilterService } from '@app-services/filter.service';
import { SideFilterService } from '@app-services/side-filter.service';
import { Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import _ from 'underscore';
import {UniversalStorage} from "@aws-amplify/core";

export class SideFilterConfigOptions {

  showProofs:boolean = true;
  showStatus:boolean = true;
  showStatusDefault:boolean = false;
  showGenres:boolean = true;
  showAccountType:boolean = true;
  showAges:boolean = true;
  showDepartments:boolean = true;
  // Opt-in: selecting a department replaces the previous one (default keeps multi-select)
  singleDepartment:boolean = false;
  showRoles:boolean = true;
  showHealthPlans:boolean = true;
  showLocalities:boolean = true;
  // Opt-in: option lists that replace the default gender and age-range lists
  genreOptions:FilterItem[] | null = null;
  ageOptions:FilterItem[] | null = null;
  // Opt-in: warns under Departamentos that crossing with gender/age ranges can hide small samples (Flow)
  showSampleSizeHint:boolean = false;
  // Opt-in: "Risco" and "Área" groups, shown while the lists are set (Flow Evolução; applied on screen by the page)
  riskOptions:FilterItem[] | null = null;
  areaOptions:FilterItem[] | null = null;

  showThermometer:boolean = false;
  showQuiz:boolean = false;
  showSubareas:boolean = false;
  showSubareaQuestions:boolean = false;
  showVaccines:boolean = false;
  showAccessStatus:boolean = false;
  showProfile:boolean = false;
}

@Component({
    selector: 'app-side-filter',
    templateUrl: './side-filter.component.html',
    styleUrls: ['./side-filter.component.scss'],
    standalone: false
})
export class SideFilterComponent implements OnInit {

  @Output() onChange:EventEmitter<SideFilterOptions>;
  @Input() filterConfiguration:SideFilterConfigOptions;

  filterForm:UntypedFormGroup;
  filterOptions: SideFilterOptions; // FilterOptions
  isAdmin = false;

  // Comprovantes
  proofTypes:FilterItem[] = FILTER_PROOF_TYPES;
  // Status
  status:FilterItem[] = FILTER_STATUS;
  // Status Default
  statusDefault:FilterItem[] = FILTER_STATUS_DEFAULT;
  // Gênero
  genres:FilterItem[] = FILTER_GENRES;
  // Profiles
  roles:FilterItem[] = FILTER_ROLES;
  // Conta
  accountTypes:FilterItem[] = FILTER_ACCOUNT_TYPES;
  // Faixa Etária
  ages:FilterItem[] = FILTER_AGES;

  // VACINAS
  vacinnes:FilterItem[] = [];

  // Departamentos
  companyDepartments:FilterItem[] = [];
  filteredCompanyDepartments:FilterItem[] = [];
  // Cargos
  companyRoles:FilterItem[] = [];
  filteredCompanyRoles:FilterItem[] = [];
  // Operadora de Saúde
  companyHealthPlans:FilterItem[] = [];
  filteredCompanyHealthPlans:FilterItem[] = [];
  // Localidade
  companyLocalities:FilterItem[] = [];
  filteredCompanyLocalities:FilterItem[] = [];
  departmentSearchTerm = '';
  roleSearchTerm = '';
  healthPlanSearchTerm = '';
  localitySearchTerm = '';
  // Termômetro das Subáreas
  // TODO
  // Respostas do Questionário
  // TODO
  filterCompanySubscription: Subscription;
  private restorePending = false;
  // Only the newest company-lists request may fill the lists and finish the restore.
  private listsSubscriptions: Subscription[] = [];
  constructor(
      private toastrService: ToastrService,
      private sideFilterService: SideFilterService,
      private readonly route: ActivatedRoute,
      private readonly router: Router,
      private location: Location,
      private fb:UntypedFormBuilder) {
    this.onChange = new EventEmitter<SideFilterOptions>();
    this.filterConfiguration = new SideFilterConfigOptions();
  }

  ngOnDestroy(){
    this.cancelListsRequests();
    if (this.filterCompanySubscription){
      this.filterCompanySubscription.unsubscribe();
    }
  }

  ngOnInit(): void {
		var universalStorage = new UniversalStorage();
		if (universalStorage.getItem('IsAdminType') === 'true') {
			this.isAdmin = true;
		}

    if (this.filterConfiguration?.genreOptions) {
      this.genres = this.filterConfiguration.genreOptions;
    }
    if (this.filterConfiguration?.ageOptions) {
      this.ages = this.filterConfiguration.ageOptions;
    }

    this.filterCompanySubscription = FilterService.onChangesFilterCompany.subscribe(filter => {
      if(filter.companyIds){
        this.loadCompanyFilters();
      }
    })

    const {params, queryParams, data} = this.route.snapshot;

    if(this.filterConfiguration.showVaccines) {
      this.loadVaccines();
    }

    const restored = this.initFilterOptions(queryParams);
    const restoredDepartments = restored.companyDepartments ? [...restored.companyDepartments] : undefined;
    this.filterOptions = this.sanitizeRestoredFilters(restored);
    // Single-select is enforced after the lists load (finishRestore), so an unknown first id cannot hide a valid one.
    if (this.filterOptions.companyDepartments && restoredDepartments) {
      this.filterOptions.companyDepartments = restoredDepartments;
    }

    this.buildForm();

    // Restored department/locality ids are validated against the loaded lists, so the first apply waits for them.
    this.restorePending = (this.filterOptions.companyDepartments?.length ?? 0) > 0
      || (this.filterOptions.companyLocalities?.length ?? 0) > 0;

    const waitingForLists = this.restorePending;
    this.loadCompanyFilters();

    if (!waitingForLists && Object.values(queryParams).length > 0) {
      this.doApply();
    }
  }

  // Keeps only the restored ids that exist in the loaded lists, then runs the first apply (once).
  private finishRestore() {
    if (!this.restorePending) {
      return;
    }
    this.restorePending = false;
    this.pruneRestored('companyDepartments', this.companyDepartments);
    this.pruneRestored('companyLocalities', this.companyLocalities);
    // Single-select keeps one department, picked among the ones that exist (the sanitizer ran before the lists loaded).
    const departments = this.filterForm.get('companyDepartments') as UntypedFormArray;
    if (this.filterConfiguration?.singleDepartment) {
      while (departments.length > 1) departments.removeAt(1);
    }
    this.doApply();
  }

  private pruneRestored(controlName:string, available:FilterItem[]) {
    const options = this.filterForm.get(controlName) as UntypedFormArray;
    const valid = new Set(available.map(item => item.value));
    for (let i = options.length - 1; i >= 0; i--) {
      if (!valid.has(options.at(i).value)) {
        options.removeAt(i);
      }
    }
  }

  private loadVaccines() {
    this.sideFilterService.loadVacinnes().subscribe( (vaccs:FilterItem[]) => {
      this.vacinnes = vaccs;
    });
  }

  openFilterModal() {
		this.sideFilterService.changeToogleSidePanelFilterSource(true, []);
	}

  closeModal() {
		this.sideFilterService.changeToogleSidePanelFilterSource(false);
	}

  doApply() {

    this.filterOptions = this.filterForm.value;

    const { params, data } = this.route.snapshot;
		let newParams = Object.assign({}, params);
		let newQueryParams: any = new Object();

    newQueryParams = this.filterOptions;

    let updateLocation = this.router.url.split('?')[0];
//     let updateLocation = this.router.url;

    const url = this.router
			.createUrlTree([updateLocation], { relativeTo: this.route, queryParams: { ...newQueryParams } })
			.toString();
		this.location.go(url);

    this.onChange.emit(this.buildFilter());

		this.closeModal();
	}

  getFilterOptions():SideFilterOptions {
    return this.filterOptions;
  }

  buildFilter():FilterOptions {

    let filter = new FilterOptions();

    try {

      if(this.filterOptions.proofTypes.length > 0) {
        filter.proofTypes = this.filterOptions.proofTypes;
      }

    }
    catch(e) {
      // ignore
    }

    try {
      if(this.filterOptions.status.length == 1) {
        filter.active = this.filterOptions.status[0] == FILTER_STATUS[0].value;
      }
      else {
        filter.active = null;
      }
    }
    catch(e) {
      // ignore
    }

    try {
      if(this.filterOptions.statusDefault.length == 1) {
        filter.active = this.filterOptions.statusDefault[0] == FILTER_STATUS_DEFAULT[0].value;
      }
      else {
        filter.active = null;
      }
    }
    catch(e) {
      // ignore
    }

    try {

      if(this.filterOptions.genres.length > 0) {
        filter.genders = this.filterOptions.genres;
      }

    }
    catch(e) {
      // ignore
    }

    try {

      if(this.filterOptions.roles.length > 0) {
        filter.roles = this.filterOptions.roles;
      }

    }
    catch(e) {
      // ignore
    }

    try {
      if(this.filterOptions.accountTypes.length == 1) {
        filter.isDependent = this.filterOptions.accountTypes[0] == FILTER_ACCOUNT_TYPES[1].value;
      }
      else {
        filter.isDependent = null;
      }
    }
    catch(e) {
      // ignore
    }

    try {
      if(this.filterOptions.ages.length > 0) {
        filter.ageRanges = this.filterOptions.ages;
      }
    }
    catch(e) {
      // ignore
    }

    try {
      if(this.filterOptions.vaccines.length > 0) {
        filter.vaccineIds = this.filterOptions.vaccines;
      }
    }
    catch(e) {
      // ignore
    }

    try {
      if(this.filterOptions.companyDepartments.length > 0) {
        filter.departmentsIds = this.filterOptions.companyDepartments;
      }
    }catch(e) {
      // ignore
    }
    try {
      if(this.filterOptions.companyRoles.length > 0) {
        filter.rolesIds = this.filterOptions.companyRoles;
      }
    }catch(e) {
      // ignore
    }
    try {
      if(this.filterOptions.companyHealthPlans.length > 0) {
        filter.healthPlansIds = this.filterOptions.companyHealthPlans;
      }
    }catch(e) {
      // ignore
    }
    try {
      if(this.filterOptions.companyLocalities.length > 0) {
        filter.localitiesIds = this.filterOptions.companyLocalities;
      }
    }catch(e) {
      // ignore
    }
    if((this.filterOptions.riskLevels ?? []).length > 0) {
      filter.riskLevels = this.filterOptions.riskLevels;
    }
    if((this.filterOptions.areas ?? []).length > 0) {
      filter.areaIds = this.filterOptions.areas;
    }
    var companyIds = this.sideFilterService.getCompanyIds();
    if(companyIds) {
      filter.companyIds = companyIds;
    }


    return filter;
  }

  clearAllItem(controlName:string) {
    const options = this.filterForm.get(controlName) as UntypedFormArray;
    options.value.forEach(value => {
      const index = options.controls.findIndex(control => control.value === value);
      options.removeAt(index);
    })

  }

  onSearchItems(filterType:'companyDepartments'|'companyRoles'|'companyHealthPlans'|'companyLocalities', event: Event) {
    const value = (event.target as HTMLInputElement).value || '';
    this.setSearchTerm(filterType, value);
    this.applySearchFilters();
  }

  clearSearch(filterType:'companyDepartments'|'companyRoles'|'companyHealthPlans'|'companyLocalities') {
    this.setSearchTerm(filterType, '');
    this.applySearchFilters();
  }

  itemChecked(controlName:string, value:any) {
		const options = this.filterForm.get(controlName) as UntypedFormArray;

    if(options == null || options.value == null) {
      return false;
    }

		return options.controls.filter(control => control.value === value).length > 0;

	}

	onChangeItem(controlName:string, event) {
		const { source, checked } = event;
		const { value } = source;
		const options = this.filterForm.get(controlName) as UntypedFormArray;

		if (checked) {
			if (controlName === 'companyDepartments' && this.filterConfiguration?.singleDepartment) {
				options.clear();
			}
			options.push(this.fb.control(value));
		} else {
			const index = options.controls.findIndex(control => control.value === value);
			options.removeAt(index);
		}
	}

  private cancelListsRequests() {
    this.listsSubscriptions.forEach(sub => sub.unsubscribe());
    this.listsSubscriptions = [];
  }

  private loadCompanyFilters() {
    this.cancelListsRequests();

    this.companyDepartments = [];
    this.filteredCompanyDepartments = [];
    this.companyRoles = [];
    this.filteredCompanyRoles = [];
    this.companyHealthPlans = [];
    this.filteredCompanyHealthPlans = [];
    this.companyLocalities = [];
    this.filteredCompanyLocalities = [];
    var companyMatrizIds = [];

    companyMatrizIds = this.sideFilterService.getCompanyMatrizIds();
    if(companyMatrizIds.length == 0){
      companyMatrizIds.push(this.sideFilterService.getCompanyIds()[0]);
    }
    if(companyMatrizIds.length < 2){
        companyMatrizIds.forEach(id => {
          this.listsSubscriptions.push(this.sideFilterService.getCompanyById(id).subscribe(response => {
            // A malformed payload must not stop the first apply: the lists stay as loaded and the restore still runs.
            try {
              const c = (response?.[0] ?? {}) as any;
              const toItems = (list: unknown): FilterItem[] =>
                (Array.isArray(list) ? list : [])
                  .filter(entry => entry != null && entry.id != null && entry.name != null)
                  .map(entry => ({ value: entry.id, label: entry.name }));

              this.companyDepartments.push(...toItems(c.companyDepartaments));
              this.companyRoles.push(...toItems(c.companyRoles));
              this.companyHealthPlans.push(...toItems(c.companyHealthPlans));
              this.companyLocalities.push(...toItems(c.companyLocalities));

              if (c.companyGenderList && c.companyGenderList.length > 0 && !this.isAdmin) {
                this.genres = (this.filterConfiguration?.genreOptions ?? FILTER_GENRES).filter(gender => c.companyGenderList.includes(gender.value));
              }
            } finally {
              this.applySearchFilters();
              this.finishRestore();
            }
          }, () => this.finishRestore()))
        })
    } else {
      this.toastrService.error('Muitas empresas Matriz selecionadas. Para melhor visualização dos filtros, selecione somente empresa Matriz!', null, {
        progressBar: false
      });
      this.finishRestore();
    }

  }

  private applySearchFilters() {
    this.filteredCompanyDepartments = this.filterItems(this.companyDepartments, this.departmentSearchTerm);
    this.filteredCompanyRoles = this.filterItems(this.companyRoles, this.roleSearchTerm);
    this.filteredCompanyHealthPlans = this.filterItems(this.companyHealthPlans, this.healthPlanSearchTerm);
    this.filteredCompanyLocalities = this.filterItems(this.companyLocalities, this.localitySearchTerm);
  }

  private setSearchTerm(filterType:'companyDepartments'|'companyRoles'|'companyHealthPlans'|'companyLocalities', value: string) {
    if (filterType === 'companyDepartments') {
      this.departmentSearchTerm = value;
    }

    if (filterType === 'companyRoles') {
      this.roleSearchTerm = value;
    }

    if (filterType === 'companyHealthPlans') {
      this.healthPlanSearchTerm = value;
    }

    if (filterType === 'companyLocalities') {
      this.localitySearchTerm = value;
    }
  }

  private filterItems(items: FilterItem[], searchTerm: string): FilterItem[] {
    const normalizedSearchTerm = this.normalizeText(searchTerm);

    if (!normalizedSearchTerm) {
      return [...items];
    }

    return items.filter(item => this.normalizeText(item.label).includes(normalizedSearchTerm));
  }

  private normalizeText(value: string): string {
    return (value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }

  // Drops filters the screen hides (a bookmarked URL must not send them) and keeps one department when single-select.
  sanitizeRestoredFilters(filter:SideFilterOptions):SideFilterOptions {
    const cfg = this.filterConfiguration;
    if (!cfg) {
      return filter;
    }
    const hidden: [boolean, string[]][] = [
      [cfg.showGenres, ['genres']],
      [cfg.showAges, ['ages']],
      [cfg.showAccountType, ['accountTypes']],
      [cfg.showStatus, ['status']],
      [cfg.showStatusDefault, ['statusDefault']],
      [cfg.showProfile, ['roles']],
      [cfg.showRoles, ['companyRoles']],
      [cfg.showLocalities, ['companyLocalities']],
      [cfg.showHealthPlans, ['companyHealthPlans']],
      [cfg.showDepartments, ['companyDepartments']],
    ];
    hidden.forEach(([visible, keys]) => {
      if (!visible) {
        keys.forEach(k => delete filter[k]);
      }
    });
    if (cfg.singleDepartment && filter.companyDepartments && filter.companyDepartments.length > 1) {
      filter.companyDepartments = [filter.companyDepartments[0]];
    }
    return filter;
  }

  private initFilterOptions(queryParams:Params):SideFilterOptions {

    let filter:SideFilterOptions = {};

    if(queryParams.proofTypes) {
      filter.proofTypes = _.isArray(queryParams.proofTypes)?[...queryParams.proofTypes]:[queryParams.proofTypes];
    }

    if(queryParams.status) {
      filter.status = _.isArray(queryParams.status)?[...queryParams.status]:[queryParams.status];
    }else {
      filter.status = [FILTER_STATUS[0].value];
    }

    if(queryParams.statusDefault) {
      filter.status = _.isArray(queryParams.statusDefault)?[...queryParams.statusDefault]:[queryParams.statusDefault];
    }else {
      filter.status = [FILTER_STATUS_DEFAULT[0].value];
    }

    if(queryParams.genres) {
      filter.genres = _.isArray(queryParams.genres)?[...queryParams.genres]:[queryParams.genres];
    }

    if(queryParams.profiles) {
      filter.roles = _.isArray(queryParams.roles)?[...queryParams.roles]:[queryParams.profiles];
    }

    if(queryParams.accountTypes) {
      filter.accountTypes = _.isArray(queryParams.accountTypes)?[...queryParams.accountTypes]:[queryParams.accountTypes];
    }

    if(queryParams.ages) {
      filter.ages = _.isArray(queryParams.ages)?[...queryParams.ages]:[queryParams.ages];
    }

    if(queryParams.companyDepartments) {
      filter.companyDepartments = _.isArray(queryParams.companyDepartments)?[...queryParams.companyDepartments]:[queryParams.companyDepartments];
    }

    if(queryParams.companyLocalities) {
      filter.companyLocalities = _.isArray(queryParams.companyLocalities)?[...queryParams.companyLocalities]:[queryParams.companyLocalities];
    }

    return filter;
  }

  private buildForm() {

    this.filterForm = this.fb.group({
      proofTypes: this.fb.array(this.filterOptions&&this.filterOptions.proofTypes?this.filterOptions.proofTypes:[]), // comprovantes
      status: this.fb.array(this.filterOptions&&this.filterOptions.status?this.filterOptions.status:[]), // status
      statusDefault: this.fb.array(this.filterOptions&&this.filterOptions.statusDefault?this.filterOptions.statusDefault:[]), // status
      genres: this.fb.array(this.filterOptions&&this.filterOptions.genres?this.filterOptions.genres:[]),// genero
      roles: this.fb.array(this.filterOptions&&this.filterOptions.roles?this.filterOptions.roles:[]),// genero
      accountTypes: this.fb.array(this.filterOptions&&this.filterOptions.accountTypes?this.filterOptions.accountTypes:[]), // conta
      ages: this.fb.array(this.filterOptions&&this.filterOptions.ages?this.filterOptions.ages:[]), // faixa etaria
      companyDepartments: this.fb.array(this.filterOptions&&this.filterOptions.companyDepartments?this.filterOptions.companyDepartments:[]), // departamentos
      companyRoles: this.fb.array(this.filterOptions&&this.filterOptions.companyRoles?this.filterOptions.companyRoles:[]), // cargos
      companyHealthPlans: this.fb.array(this.filterOptions&&this.filterOptions.companyHealthPlans?this.filterOptions.companyHealthPlans:[]), // operadora de saude
      companyLocalities: this.fb.array(this.filterOptions&&this.filterOptions.companyLocalities?this.filterOptions.companyLocalities:[]), // localidade
      vaccines: this.fb.array(this.filterOptions&&this.filterOptions.vaccines?this.filterOptions.vaccines:[]), // vaccines
      riskLevels: this.fb.array(this.filterOptions&&this.filterOptions.riskLevels?this.filterOptions.riskLevels:[]), // risco (Flow Evolução)
      areas: this.fb.array(this.filterOptions&&this.filterOptions.areas?this.filterOptions.areas:[]), // área (Flow Evolução)

    });

  }

}
